import DOMPurify from 'dompurify';

// Video embeds are the only iframes the app legitimately renders today
// (backend-generated Vimeo player embeds in the VideoBlock renderers).
// No stored rich-text / discussion content embeds iframes, so we allow
// iframes ONLY from these video hosts and strip every other iframe.
const ALLOWED_IFRAME_HOSTS = [
  'youtube.com',
  'www.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
  'player.vimeo.com',
];

let hooksInstalled = false;

function installHooks() {
  if (hooksInstalled) return;
  hooksInstalled = true;

  // Remove iframes whose src host is not an approved video host.
  DOMPurify.addHook('uponSanitizeElement', (node, data) => {
    if (data.tagName !== 'iframe') return;
    const src = node.getAttribute && node.getAttribute('src');
    let ok = false;
    try {
      const base = typeof window !== 'undefined' ? window.location.origin : 'https://localhost';
      const url = new URL(src || '', base);
      ok = url.protocol === 'https:' && ALLOWED_IFRAME_HOSTS.includes(url.hostname);
    } catch (e) {
      ok = false;
    }
    if (!ok && node.parentNode) {
      node.parentNode.removeChild(node);
    }
  });

  // Any link opening in a new tab must not leak the opener.
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
}

// Sanitize untrusted HTML before it reaches dangerouslySetInnerHTML.
// Uses DOMPurify's default safe profile, which already permits the
// formatting the app relies on (headings, lists, tables, code/pre, links,
// images, blockquote, span/div and sanitized inline styles) while removing
// <script>, event handlers (onerror/onclick/...) and javascript: URLs.
// iframes are additionally allowed but restricted to video hosts above.
export function sanitizeHtml(html) {
  if (html == null) return '';
  installHooks();
  return DOMPurify.sanitize(String(html), {
    ADD_TAGS: ['iframe'],
    ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'target'],
    ALLOW_DATA_ATTR: false,
  });
}

// Remove unfilled image placeholders (<img> whose src is not a real image URL)
// so LEARNERS never see a broken/placeholder image. This is a RENDER-TIME
// filter only — it does NOT modify stored html, so the course-builder editor
// and "Regen Images" still see the placeholders in the saved content and can
// fill them in later. A src is "real" only if it is a data/blob URL, an http(s)
// URL, or an /api path and does not contain the word "placeholder".
export function stripPlaceholderImages(html) {
  if (html == null) return '';
  try {
    const doc = new DOMParser().parseFromString(String(html), 'text/html');
    doc.querySelectorAll('img').forEach((img) => {
      const src = (img.getAttribute('src') || '').trim();
      const lower = src.toLowerCase();
      const isReal = !!src && src !== '#' && !lower.includes('placeholder') &&
        (lower.startsWith('http') || src.startsWith('/api') || lower.startsWith('data:') || lower.startsWith('blob:'));
      if (!isReal) {
        const fig = img.closest('figure');
        if (fig && fig.querySelectorAll('img').length === 1) fig.remove();
        else img.remove();
      }
    });
    return doc.body.innerHTML;
  } catch (e) {
    return String(html);
  }
}

export default sanitizeHtml;
